/**
 * Firebase Cloud Messaging (FCM) client init.
 * Lazy-initialized so the rest of the app loads instantly even if Firebase config is missing.
 */
import { initializeApp, getApps } from 'firebase/app';
import { getMessaging, getToken, onMessage, isSupported } from 'firebase/messaging';
import axios from 'axios';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY;

let _app = null;
let _messaging = null;
let _onMessageBound = false;

function isConfigured() {
  return Boolean(firebaseConfig.apiKey && firebaseConfig.messagingSenderId && VAPID_KEY);
}

function base64UrlToUint8Array(base64Url) {
  const padding = '='.repeat((4 - (base64Url.length % 4)) % 4);
  const base64 = (base64Url + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

function subscriptionMatchesVapid(subscription, vapidKey) {
  try {
    const current = subscription.options && subscription.options.applicationServerKey;
    if (!current) return false;
    const a = new Uint8Array(current);
    const b = base64UrlToUint8Array(vapidKey);
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
    return true;
  } catch (_) { return false; }
}

async function getMessagingInstance() {
  if (!isConfigured()) return null;
  if (!(await isSupported().catch(() => false))) return null;
  if (!_app) _app = getApps()[0] || initializeApp(firebaseConfig);
  if (!_messaging) _messaging = getMessaging(_app);
  return _messaging;
}

/** Checks if the browser/device supports push notifications at all. */
export async function pushSupported() {
  try {
    if (!('Notification' in window)) return false;
    if (!('serviceWorker' in navigator)) return false;
    return await isSupported();
  } catch (_) { return false; }
}

/** Current permission: 'granted' | 'denied' | 'default' | 'unsupported' */
export function pushPermission() {
  if (typeof Notification === 'undefined') return 'unsupported';
  return Notification.permission;
}

/**
 * Ask the user for permission, register the FCM SW, get a token and POST it to the backend.
 * Idempotent — safe to call multiple times.
 */
export async function enablePushNotifications() {
  if (!(await pushSupported())) return { ok: false, reason: 'unsupported' };
  if (!isConfigured()) return { ok: false, reason: 'not-configured' };
  // 1. Permission
  const perm = await Notification.requestPermission();
  if (perm !== 'granted') return { ok: false, reason: 'permission-denied' };
  // 2. Register FCM SW
  const reg = await navigator.serviceWorker.register('/firebase-messaging-sw.js', { scope: '/' })
    .catch((e) => { console.warn('FCM SW register failed:', e); return null; });
  if (!reg) return { ok: false, reason: 'sw-register-failed' };
  // 3. Wait until the SW is fully ready and controlling pages.
  // This is the canonical fix for `messaging/token-subscribe-failed: body stream already read`.
  try {
    await navigator.serviceWorker.ready;
  } catch (_) { /* ignore */ }
  // 4. Drop the push subscription ONLY if it was created with a different VAPID key.
  // Unsubscribing unconditionally invalidated the previous token on every page load,
  // which left devices with 0 registered tokens whenever getToken failed afterwards.
  try {
    const existing = await reg.pushManager.getSubscription();
    if (existing && !subscriptionMatchesVapid(existing, VAPID_KEY)) {
      await existing.unsubscribe().catch(() => false);
    }
  } catch (_) { /* ignore */ }
  // 5. Brief settle to avoid the race where pushManager.subscribe() is called too soon
  await new Promise((r) => setTimeout(r, 500));
  // 6. Get token with up to 2 retries on transient subscribe failure
  const messaging = await getMessagingInstance();
  if (!messaging) return { ok: false, reason: 'messaging-init-failed' };
  let token;
  let lastError = null;
  for (let attempt = 0; attempt < 3 && !token; attempt++) {
    try {
      token = await getToken(messaging, { vapidKey: VAPID_KEY, serviceWorkerRegistration: reg });
    } catch (e) {
      lastError = e;
      const msg = String((e && (e.message || e.code)) || e).toLowerCase();
      console.warn(`FCM getToken attempt ${attempt + 1} failed:`, e);
      // Retry only on the known transient body-stream bug
      if (msg.includes('body stream') || msg.includes('subscribe-failed') || msg.includes('aborted')) {
        await new Promise((r) => setTimeout(r, 1000 * (attempt + 1)));
        continue;
      }
      break;
    }
  }
  if (!token) {
    const msg = String((lastError && (lastError.message || lastError)) || 'unknown');
    return { ok: false, reason: 'token-failed', error: msg };
  }
  // 7. POST to backend
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
  try {
    await axios.post(`${API}/notificacoes/registar-token`, { token, platform: 'web' });
  } catch (e) {
    console.warn('Register token API failed:', e);
    return { ok: false, reason: 'backend-register-failed', error: String(e) };
  }
  // 8. Bind foreground message handler once
  if (!_onMessageBound) {
    _onMessageBound = true;
    onMessage(messaging, (payload) => {
      const n = payload.notification || {};
      const data = payload.data || {};
      try {
        window.dispatchEvent(new CustomEvent('animalog:push', { detail: { notification: n, data } }));
      } catch (_) { /* ignore */ }
      try {
        if (Notification.permission === 'granted') {
          new Notification(n.title || 'AnimaLog', { body: n.body || '', icon: '/favicon.png', data });
        }
      } catch (_) { /* ignore */ }
    });
  }
  return { ok: true, token };
}

export { isConfigured as isFirebaseConfigured };
